import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Star, Shield, Clock, Award } from "lucide-react";
import { useBookingModal } from "../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, -50]);
  const y2 = useTransform(scrollY, [0, 300], [0, -100]);
  const { openModal } = useBookingModal();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-linear-to-br from-gray-900 via-gray-800 to-secondary">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Gradient Orbs */}
        <motion.div
          className="absolute top-20 left-20 w-96 h-96 bg-linear-to-r from-primary/20 to-primary-light/20 rounded-full blur-3xl"
          animate={{
            x: mousePosition.x * 0.02,
            y: mousePosition.y * 0.02,
          }}
          transition={{ type: "spring", stiffness: 50, damping: 30 }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-80 h-80 bg-linear-to-r from-warning/20 to-primary/20 rounded-full blur-3xl"
          animate={{
            x: mousePosition.x * -0.015,
            y: mousePosition.y * -0.015,
          }}
          transition={{ type: "spring", stiffness: 50, damping: 30 }}
        />

        {/* Floating Particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-white/10 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.1, 0.5, 0.1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
          {/* Left Content */}
          <motion.div style={{ y: y1 }} className="text-white space-y-8">
            {/* Premium Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-xs border border-white/20 rounded-full px-6 py-3"
            >
              <Star className="h-5 w-5 text-warning fill-current" />
              <span className="text-sm font-medium">
                {t("1047", "Premium Transportation Service")}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-5xl md:text-7xl font-bold leading-tight"
            >
              {t("1048", "Book Your")}{" "}
              <span className="relative inline-block">
                <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                  {t("1049", "Luxury")}
                </span>
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-linear-to-r from-primary to-primary-light rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8 }}
                />
              </span>{" "}
              {t("1050", "Taxi")} <br className="md:hidden" />
              {t("1051", "Today!")}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-xl md:text-2xl text-gray-100 leading-relaxed max-w-lg"
            >
              {t(
                "1052",
                "Experience premium transportation with our Mercedes-Benz fleet. Professional, reliable, and always on time."
              )}
            </motion.p>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={openModal}
                className="group relative overflow-hidden bg-linear-to-r from-primary to-primary-light hover:from-primary-dark hover:to-primary text-white px-8 py-4 rounded-2xl text-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center justify-center"
              >
                <motion.div
                  className="absolute inset-0 bg-white/20"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.6 }}
                />
                <span className="relative z-10 flex items-center">
                  {t("1053", "Book Premium Ride")}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>

              <a
                href="tel:+31639182624"
                className="group border-2 border-white/30 backdrop-blur-xs text-white hover:bg-gray-800 hover:text-secondary px-8 py-4 rounded-2xl text-lg font-semibold transition-all duration-300 flex items-center justify-center hover:shadow-xl"
              >
                <Phone className="mr-2 h-5 w-5" />
                <span>+31 6 39182624</span>
              </a>
            </motion.div>

            {/* Feature Pills */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-wrap gap-4"
            >
              {[
                { icon: Shield, text: t("1054", "Fully Insured") },
                { icon: Clock, text: t("1055", "24/7 Available") },
                { icon: Award, text: t("1056", "Premium Service") },
              ].map((feature) => (
                <motion.div
                  key={feature.text}
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center space-x-2 bg-white/10 backdrop-blur-xs border border-white/20 rounded-full px-4 py-2"
                >
                  <feature.icon className="h-4 w-4 text-warning" />
                  <span className="text-sm font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Interactive Vehicle Showcase */}
          <motion.div style={{ y: y2 }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
              className="relative"
            >
              {/* Main Vehicle Image */}
              <div className="relative group">
                <motion.img
                  src="/images/ab.webp"
                  alt={t("1057", "Luxury Mercedes Taxi")}
                  className="rounded-3xl shadow-2xl w-full h-auto transform group-hover:scale-105 transition-transform duration-700"
                  whileHover={{ scale: 1.02 }}
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-linear-to-t from-blue-900/40 via-transparent to-transparent rounded-3xl" />

                {/* Floating Info Cards */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="absolute top-6 left-6 bg-gray-800/95 backdrop-blur-xs p-4 rounded-2xl shadow-xl"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    <div>
                      <div className="font-semibold text-white text-sm">
                        {t("1058", "Available Now")}
                      </div>
                      <div className="text-xs text-gray-200">
                        {t("1059", "Mercedes-Benz Fleet")}
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="absolute bottom-6 right-6 bg-linear-to-r from-primary to-primary-light text-white p-4 rounded-2xl shadow-xl"
                >
                  <div className="text-right">
                    <div className="font-bold text-lg">
                      {t("1060", "From €50")}
                    </div>
                    <div className="text-sm opacity-90">
                      {t("1061", "Fixed Pricing")}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Decorative Elements */}
              <motion.div
                className="absolute -top-4 -right-4 w-24 h-24 bg-linear-to-r from-primary to-primary-light rounded-full opacity-20 blur-xl"
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              <motion.div
                className="absolute -bottom-4 -left-4 w-32 h-32 bg-linear-to-r from-accent to-primary-light rounded-full opacity-20 blur-xl"
                animate={{
                  scale: [1.2, 1, 1.2],
                  rotate: [360, 180, 0],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
